import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";

/**
 * safeDecodeURIComponent -- decodes a single URI segment, returning raw on malformed %
 */
export function safeDecodeURIComponent(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * parseHashSegments -- splits a hash string into decoded path segments.
 *
 * Used as the source-of-truth for route resolution. Both the wouter location
 * and the raw window.location.hash ultimately derive from the same hash, but
 * we read the hash directly here so the hook works in test environments where
 * no <Router> context is present.
 */
function parseHashSegments(hash: string): string[] {
  const raw = hash.replace(/^#\/?/, "");
  if (!raw) return [];
  return raw.split("/").map(safeDecodeURIComponent);
}

// Pages that are NOT the dashboard (top-level navigation targets)
const TOP_LEVEL_PAGES = new Set([
  "dev-server", "users", "api-keys", "sessions",
  "fleet", "leaderboard", "template-library", "templates", "costs",
]);

type Tab = "graph" | "list" | "assets" | "settings";
const VALID_TABS = new Set<string>(["graph", "list", "assets", "settings"]);

type RouteState = {
  /** Current top-level page (e.g. "dashboard", "fleet", "dev-server") */
  page: string;
  /** Selected project ID parsed from #/project/<id> routes (legacy) */
  selectedProjectId: string | undefined;
  /** Selected repo ID parsed from #/repo-home/<slug> routes (legacy) */
  selectedRepoId: string | undefined;
  /** Active dashboard tab */
  tab: Tab;
  /** Whether to hide completed tasks in graph/list views */
  hideComplete: boolean;
  /** Raw URL segments for downstream resolution */
  segments: string[];
  /** Set the active tab (also updates URL if on a project route) */
  setTab: (t: Tab) => void;
  /** Toggle hideComplete */
  toggleHideComplete: () => void;
};

/**
 * useRouteState -- encapsulates hash-based routing (FR-D08 / FR-023)
 *
 * Wraps wouter's useLocation for navigation, but reads window.location.hash
 * directly for route resolution. This dual approach ensures:
 * - Navigation works through wouter (useHashLocation via Router)
 * - Route parsing works in test environments (no Router context needed)
 * - No manual parseHash() cascade -- all route types handled uniformly
 *
 * Supports legacy hash formats (#/project/<id>, #/repo-home/<slug>)
 * for backward compatibility with tests.
 */
export function useRouteState(): RouteState {
  const [, navigate] = useLocation();
  const [hideComplete, setHideComplete] = useState(false);

  // Track raw hash for route resolution (works with or without Router)
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Parse hash into decoded segments
  const parts = parseHashSegments(hash);

  // Derive page from the first segment
  const firstSegment = parts[0] || "home";
  const page = (() => {
    if (TOP_LEVEL_PAGES.has(firstSegment)) return firstSegment;
    // Legacy routes: #/project/<id> and #/repo-home/<slug>
    if (firstSegment === "project") return "project";
    if (firstSegment === "repo-home") return "repo-home";
    // Default: dashboard (path is /{repoSlug}/... or /)
    if (firstSegment === "home") return "dashboard";
    return "dashboard";
  })();

  // Derive selected IDs from legacy hash format
  let selectedProjectId: string | undefined;
  let selectedRepoId: string | undefined;

  if (firstSegment === "project" && parts.length > 1) {
    selectedProjectId = parts[1];
  } else if (firstSegment === "repo-home" && parts.length > 1) {
    selectedRepoId = parts.slice(1).join("/");
  }

  // Derive tab from URL segments (wouter format: /{repoSlug}/{projectName}/{tab})
  const urlTab = parts.length >= 3 && VALID_TABS.has(parts[2]) ? (parts[2] as Tab) : undefined;
  const [localTab, setLocalTab] = useState<Tab>("graph");
  const effectiveTab = urlTab ?? localTab;

  // Sync localTab when URL tab changes
  useEffect(() => {
    if (urlTab) setLocalTab(urlTab);
  }, [urlTab]);

  const setTab = useCallback(
    (t: Tab) => {
      setLocalTab(t);
      // If we're on a project route (>= 2 segments), update URL
      if (parts.length >= 2 && page === "dashboard") {
        navigate(`/${encodeURIComponent(parts[0])}/${encodeURIComponent(parts[1])}/${t}`);
      }
    },
    [parts, page, navigate]
  );

  const toggleHideComplete = useCallback(() => {
    setHideComplete((prev) => !prev);
  }, []);

  return {
    page,
    selectedProjectId,
    selectedRepoId,
    tab: effectiveTab,
    hideComplete,
    segments: parts,
    setTab,
    toggleHideComplete,
  };
}
